// components
import {
  CasRenderDate,
  CasCRUDTable,
  CasRenderBoolTrue,
  CasRenderArrayAsRows,
} from 'src/compass/shared/components';

// hooks
import {
  AdditClearingSelect,
  EntityInfoCMEAccounts,
  useNewUnassignedOptions,
  SALES_DASHBOARD_UNASSIGNED,
} from 'src/compass/application/commissions';
import {
  CasRiskManagerSelect,
  CasRiskWithSalesManagerSelect,
  CasSourceSelect,
  CasYesNoSelect,
} from 'src/compass/application';
import { usePeriodDates } from 'src/compass/shared/hooks/usePeriodDates';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';

import { CasRenderAsOfStringLeft } from '../CasAsOf/CasRenderAsOfStringLeft';
import { AsOfDateSelect } from './_internal/AsOfDateSelect';
import { CMEScheduleSelect } from './_internal/CMEScheduleSelect';
import { SalesCodeSelect } from './_internal/SalesCodeSelect';

interface IProps {
  isCommissionContext?: boolean;
}

export default function CasCmeAccountsTable({ isCommissionContext = true }: IProps) {
  const [unassignedOptions, isLoadingUnassignedOptions] = useNewUnassignedOptions();

  const { data, isLoading } = usePeriodDates();

  if (isLoading || isLoadingUnassignedOptions) return <CasLoader />;
  return (
    <CasCRUDTable
      {...EntityInfoCMEAccounts}
      columns={[
        {
          field: 'company',
          label: 'Name',
          showEditOnclick: true,
        },
        {
          field: 'accountNumber',
          label: 'Account#',
        },
        {
          field: 'broker',
          label: 'Relationship Owner',
        },
        {
          field: 'customer',
          label: 'Customer',
        },
        {
          field: 'cmeCommSchedule',
          label: 'Schedule',
          sortable: false,
          render: CasRenderAsOfStringLeft,
        },
        {
          field: 'source',
          sortable: false,
          render: CasRenderArrayAsRows,
        },
        {
          field: 'type',
          sortable: false,
          render: CasRenderArrayAsRows,
        },
        {
          field: 'cmeAdditClearing',
          label: 'Addit',
          sortable: false,
        },
        ...(isCommissionContext
          ? [
              {
                field: 'agreement',
                sortable: false,
              },
            ]
          : []),
        {
          field: 'opened',
          label: 'Date Open',
          id: 'opened',
          render: CasRenderDate,
        },
        {
          field: 'officeSalesCode',
          label: 'Office-Sales',
        },
        {
          field: 'isClosed',
          label: 'Closed',
          render: CasRenderBoolTrue,
        },
      ]}
      filters={[
        {
          name: 'company',
          label: 'Name',
        },
        {
          name: 'accountNumber',
          label: 'Account#',
        },
        {
          type: 'component',
          name: 'brokerId',
          label: 'Relationship Owner',
          includeAllOption: true,
          component: CasRiskManagerSelect,
        },
        {
          type: 'autocomplete',
          name: 'entityId',
          label: 'Customer',
          url: 'Utilities/Entity/List',
        },
        {
          type: 'component',
          name: 'schedule',
          includeAllOption: true,
          component: CMEScheduleSelect,
        },
        {
          type: 'component',
          name: 'sourceEmployeeId',
          label: 'Source',
          includeAllOption: true,
          component: CasRiskWithSalesManagerSelect,
        },
        {
          type: 'component',
          name: 'sharedSvc',
          label: 'Shared Svc',
          includeAllOption: true,
          component: CasYesNoSelect,
        },
        {
          type: 'component',
          name: 'sourceId',
          label: 'Type',
          includeAllOption: true,
          component: CasSourceSelect,
        },
        {
          type: 'component',
          name: 'cmeAdditClearingId',
          label: 'Addit',
          includeAllOption: true,
          component: AdditClearingSelect,
        },
        ...(isCommissionContext
          ? [
              {
                type: 'component',
                name: 'agreementEmployeeId',
                label: 'Agreement',
                includeAllOption: true,
                component: CasRiskWithSalesManagerSelect,
              },
            ]
          : []),
        {
          type: 'component',
          name: 'asOfDate',
          initialValue: data?.currentPeriodStart,
          component: AsOfDateSelect,
        },
        {
          type: 'select',
          name: 'newUnassigned',
          label: 'New/Unassigned',
          initialValue: isCommissionContext ? null : SALES_DASHBOARD_UNASSIGNED,
          options: unassignedOptions,
        },
        {
          type: 'component',
          name: 'salesCodeId',
          label: 'Office-Sales',
          includeAllOption: true,
          component: SalesCodeSelect,
        },
      ]}
      showAddButton={false}
    />
  );
}
